import { DateRange } from './panelTypes';

export const ANALYTICS_TIME_ZONE = 'Europe/Copenhagen';

const dateFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: ANALYTICS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: ANALYTICS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
});

const partsRecord = (formatter: Intl.DateTimeFormat, value: Date): Record<string, number> =>
    Object.fromEntries(
        formatter
            .formatToParts(value)
            .filter((part) => part.type !== 'literal')
            .map((part) => [part.type, Number(part.value)])
    );

export const calendarDateInCopenhagen = (value: Date): string => {
    const parts = partsRecord(dateFormatter, value);
    return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
};

export const shiftCalendarDate = (date: string, days: number): string => {
    const [year, month, day] = date.split('-').map(Number);
    const shifted = new Date(Date.UTC(year, month - 1, day + days));
    return shifted.toISOString().slice(0, 10);
};

// Convert a Copenhagen wall-clock time to its UTC instant. Iterating twice
// makes the conversion stable on either side of daylight-saving transitions.
const copenhagenDateTime = (
    date: string,
    hour: number,
    minute: number,
    second: number,
    millisecond: number
): Date => {
    const [year, month, day] = date.split('-').map(Number);
    const desiredAsUtc = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
    let instant = desiredAsUtc;

    for (let attempt = 0; attempt < 2; attempt += 1) {
        const actual = partsRecord(dateTimeFormatter, new Date(instant));
        const actualAsUtc = Date.UTC(
            actual.year,
            actual.month - 1,
            actual.day,
            actual.hour,
            actual.minute,
            actual.second,
            millisecond
        );
        instant += desiredAsUtc - actualAsUtc;
    }

    return new Date(instant);
};

export const rangeFromCalendarDates = (
    startDate: string,
    endDate: string,
    label = 'Custom',
    now?: Date
): DateRange => ({
    label,
    startDate,
    endDate,
    from: copenhagenDateTime(startDate, 0, 0, 0, 0).toISOString(),
    to: now
        ? now.toISOString()
        : copenhagenDateTime(endDate, 23, 59, 59, 999).toISOString(),
});

export const PRESET_RANGES: Record<string, (now?: Date) => DateRange> = {
    today: (now = new Date()) => {
        const today = calendarDateInCopenhagen(now);
        return rangeFromCalendarDates(today, today, 'Today', now);
    },
    yesterday: (now = new Date()) => {
        const yesterday = shiftCalendarDate(calendarDateInCopenhagen(now), -1);
        return rangeFromCalendarDates(yesterday, yesterday, 'Yesterday');
    },
    '7d': (now = new Date()) => {
        const today = calendarDateInCopenhagen(now);
        return rangeFromCalendarDates(shiftCalendarDate(today, -6), today, 'Last 7 days', now);
    },
    '30d': (now = new Date()) => {
        const today = calendarDateInCopenhagen(now);
        return rangeFromCalendarDates(shiftCalendarDate(today, -29), today, 'Last 30 days', now);
    },
    '90d': (now = new Date()) => {
        const today = calendarDateInCopenhagen(now);
        return rangeFromCalendarDates(shiftCalendarDate(today, -89), today, 'Last 90 days', now);
    },
};

export const defaultRange = (now = new Date()): DateRange => PRESET_RANGES.today(now);

export const rangeFromQuery = (
    startDate: string | null,
    endDate: string | null,
    label: string | null
): DateRange => {
    if (startDate && endDate) {
        return rangeFromCalendarDates(startDate, endDate, label || 'Custom');
    }
    return defaultRange();
};
